import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MessageSquareText, BarChart2, ClipboardList, Users2,
  Vote, ThumbsUp, FileBarChart, Sparkles, Plus, Radio, LineChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LandingNav from "@/components/landing/LandingNav";
import Footer from "@/components/landing/Footer";
import DashboardMockup from "@/components/landing/DashboardMockup";
import ReportMockup from "@/components/landing/ReportMockup";
import PhoneMockup from "@/components/landing/PhoneMockup";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function SectionLabel({ children }) {
  return <p className="text-xs uppercase tracking-[0.2em] font-bold text-accent mb-4">{children}</p>;
}

export default function Landing() {
  return (
    <div className="bg-background">
      <LandingNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pitso-grid-bg opacity-[0.4] [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial="hidden" animate="show" className="lg:col-span-5">
            <motion.span variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Meet. Connect. Engage.
            </motion.span>
            <motion.h1 variants={fadeUp} custom={1} className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02] text-foreground">
              Make Every Event More Engaging.
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md">
              PitsoIO helps organisers capture audience questions, participation and feedback, then turn event
              activity into clear, actionable reports.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground hover:opacity-90 transition-opacity h-12 px-6">
                <Link to="/register" data-testid="hero-create-event">Create an Event <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6">
                <a href="#how-it-works" data-testid="hero-how-it-works">See How It Works</a>
              </Button>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="lg:col-span-7">
            <DashboardMockup />
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-24">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground max-w-2xl">
            What happens in the room shouldn't disappear when the event ends.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { t: "Questions get lost.", d: "Great audience questions vanish in the noise, never captured or answered." },
              { t: "Feedback is scattered.", d: "Ratings and comments live across forms, chats and sticky notes." },
              { t: "Reports take too long.", d: "Turning raw event activity into a report costs days of manual work." },
            ].map((p, i) => (
              <div key={i} className="rounded-xl border border-border bg-background p-8">
                <span className="font-display text-4xl font-extrabold text-accent/20">0{i + 1}</span>
                <h3 className="mt-4 font-display text-xl font-bold text-foreground">{p.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT */}
      <section id="product" className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
        <div className="max-w-2xl">
          <SectionLabel>The Platform</SectionLabel>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            One platform for the conversation around your event.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: MessageSquareText, t: "Questions" },
            { icon: Vote, t: "Polls" },
            { icon: Users2, t: "Audience participation" },
            { icon: ThumbsUp, t: "Session feedback" },
            { icon: ClipboardList, t: "Post-event surveys" },
            { icon: BarChart2, t: "Analytics" },
            { icon: Sparkles, t: "AI-assisted reporting" },
            { icon: FileBarChart, t: "Executive reports" },
          ].map((f, i) => (
            <div key={i} className="group rounded-xl border border-border bg-card p-6 hover:-translate-y-1 transition-transform">
              <f.icon className="h-5 w-5 text-accent" />
              <p className="mt-4 font-display font-bold text-foreground">{f.t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-y border-border bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
          <SectionLabel>How It Works</SectionLabel>
          <div className="grid md:grid-cols-3 gap-10 mt-6">
            {[
              { icon: Plus, k: "Create", d: "Set up your event and sessions." },
              { icon: Radio, k: "Engage", d: "Let attendees ask questions, vote, respond to polls and give feedback." },
              { icon: LineChart, k: "Understand", d: "Turn event data into useful reports and insights." },
            ].map((s, i) => (
              <div key={i} className="border-t border-white/15 pt-6">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                    <s.icon className="h-4 w-4 text-white" />
                  </span>
                  <span className="font-display text-xl font-bold uppercase tracking-wide">{s.k}</span>
                </div>
                <p className="mt-4 text-white/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT PREVIEW / ORGANISERS */}
      <section id="organisers" className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>For Organisers</SectionLabel>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              A command centre for everything happening in the room.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed max-w-md">
              Monitor attendance, moderate questions, launch polls and track feedback in real time — from a single,
              clean dashboard built for live events.
            </p>
          </div>
          <DashboardMockup />
        </div>
      </section>

      {/* REPORTS */}
      <section id="reports" className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <ReportMockup className="order-2 lg:order-1" />
          <div className="order-1 lg:order-2">
            <SectionLabel>Reporting</SectionLabel>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              From event activity to an executive report.
            </h2>
            <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-muted-foreground">
              {["Executive Summary", "Key Themes", "Audience Questions", "Session Feedback", "Top Concerns", "Recommendations", "Action Items"].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* AUDIENCE PARTICIPATION */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <SectionLabel>Audience Participation</SectionLabel>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Participation in the palm of every attendee.
          </h2>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            {["View the live session", "Ask a question — anonymously if they prefer", "Upvote the questions that matter most", "Vote in live polls", "Rate every session"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="h-5 w-5 rounded-full bg-accent/10 text-accent flex items-center justify-center text-xs">✓</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <PhoneMockup />
      </section>

      {/* CTA */}
      <section id="pricing" className="border-t border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <div className="rounded-2xl bg-primary text-primary-foreground p-10 sm:p-16 text-center">
            <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight max-w-3xl mx-auto">
              Your next event should leave you with more than attendance numbers.
            </h2>
            <Button asChild size="lg" className="mt-8 rounded-full bg-accent text-accent-foreground hover:opacity-90 transition-opacity h-12 px-7">
              <Link to="/register" data-testid="cta-create-first-event">Create Your First Event <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
